import { MultiStep, Step, MultiStepItemProps } from "../MultiStepContainer";

interface UseActiveStepProps<T extends MultiStepItemProps = MultiStepItemProps> {
  steps: MultiStep<T>[];
  currentStepId: string; // "stepId" or "stepId:substepId"
}

export const useActiveStep = <
  T extends MultiStepItemProps = MultiStepItemProps
>({
  steps,
  currentStepId,
}: UseActiveStepProps<T>) => {
  // Find the active step index and substep info
  const getActiveStepInfo = () => {
    const [stepId, substepId] = currentStepId.split(":");
    const stepIdxRaw = steps.findIndex((s) => s.id === stepId);
    const stepIdx = stepIdxRaw >= 0 ? stepIdxRaw : 0;
    const currentStep = steps[stepIdx];

    // Calculate absolute position in the workflow (only counting available steps)
    let absolutePosition = 0;
    let currentSubstepIndex = 0;

    // Count all available steps/substeps before current step
    for (let i = 0; i < stepIdx; i++) {
      const step = steps[i];
      if (step.available !== false) {
        absolutePosition += step.substeps?.length ?? 1;
      }
    }

    // Add current substep position (if any) - only if current step is available
    if (
      currentStep.available !== false &&
      currentStep.substeps?.length &&
      substepId
    ) {
      const subIdx = currentStep.substeps.findIndex(
        (s: Step<T>) => s.id === substepId
      );
      currentSubstepIndex = subIdx >= 0 ? subIdx : 0;
      absolutePosition += currentSubstepIndex;
    }

    // Total possible positions (only counting available steps)
    const totalPositions = steps.reduce((total, step) => {
      if (step.available !== false) {
        return total + (step.substeps?.length ?? 1);
      }
      return total;
    }, 0);

    return {
      stepIdx,
      currentStep,
      absolutePosition,
      currentSubstepIndex,
      totalPositions,
    };
  };

  const {
    stepIdx,
    currentStep,
    absolutePosition,
    currentSubstepIndex,
    totalPositions,
  } = getActiveStepInfo();

  // Get current and next labels
  const getLabels = () => {
    let currentLabel = currentStep.label;
    let nextLabel = "";

    if (currentStep.substeps?.length) {
      const currentSubstep = currentStep.substeps[currentSubstepIndex];
      if (currentSubstep) {
        currentLabel = `${currentStep.label}: ${currentSubstep.label}`;
      }

      // Next available substep in current step
      let nextAvailableSubstepIndex = -1;
      for (let i = currentSubstepIndex + 1; i < currentStep.substeps.length; i++) {
        if (currentStep.substeps[i].available !== false) {
          nextAvailableSubstepIndex = i;
          break;
        }
      }

      if (nextAvailableSubstepIndex >= 0) {
        nextLabel = currentStep.substeps[nextAvailableSubstepIndex].label;
      } else {
        // Find next available step (and first available substep if present)
        for (let i = stepIdx + 1; i < steps.length; i++) {
          const nextStep = steps[i];
          if (nextStep.available !== false) {
            if (nextStep.substeps?.length) {
              for (let j = 0; j < nextStep.substeps.length; j++) {
                if (nextStep.substeps[j].available !== false) {
                  nextLabel = `${nextStep.label}: ${nextStep.substeps[j].label}`;
                  break;
                }
              }
            } else {
              nextLabel = nextStep.label;
            }
            break;
          }
        }
      }
    } else {
      // No substeps: find next available step (and its first available substep if present)
      for (let i = stepIdx + 1; i < steps.length; i++) {
        const nextStep = steps[i];
        if (nextStep.available !== false) {
          if (nextStep.substeps?.length) {
            for (let j = 0; j < nextStep.substeps.length; j++) {
              if (nextStep.substeps[j].available !== false) {
                nextLabel = `${nextStep.label}: ${nextStep.substeps[j].label}`;
                break;
              }
            }
          } else {
            nextLabel = nextStep.label;
          }
          break;
        }
      }
    }

    return { currentLabel, nextLabel };
  };

  const { currentLabel, nextLabel } = getLabels();

  // Calculate progress percentage
  const calculateProgress = () => {
    if (totalPositions <= 1) return 0;
    const pct = (absolutePosition / (totalPositions - 1)) * 100;
    return Math.min(Math.max(pct, 0), 100);
  };

  return {
    activeStep: stepIdx,
    totalSteps: totalPositions,
    currentLabel,
    nextLabel,
    progress: calculateProgress(),
  };
};
